/**
 * Category carousel for index Shop by Category section (Swiper).
 */
const HIDDEN_CARD_THRESHOLD = 0.1;

export class LpCategoryCarousel extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this._swiperRaf = 0;
    this._swiperWaitStart = 0;
  }

  connectedCallback() {
    this.section = this.closest('.lp-index-shop-by-category');
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');

    // data-layout comes from the section's card_layout setting: 'cro' is the
    // 4-up padded track, anything else the classic 360px-card homepage row
    // that runs off the right edge.
    this.isCro = this.dataset.layout === 'cro';
    this.desktopGroup = this.isCro ? 4 : 3;
    this.desktopPeek = !this.isCro;

    requestAnimationFrame(() => {
      this.dotsWrap = this.section?.querySelector('[data-dots]');
      this.initWhenReady();
    });
  }

  disconnectedCallback() {
    if (this._swiperRaf) cancelAnimationFrame(this._swiperRaf);
    this.destroy();
  }

  initWhenReady() {
    if (window.Swiper) {
      this.init();
      return;
    }

    const maxWaitMs = 8000;
    this._swiperWaitStart = this._swiperWaitStart || performance.now();

    const waitForSwiper = () => {
      this._swiperRaf = 0;
      if (!this.isConnected) return;
      if (window.Swiper) {
        this.init();
        return;
      }
      if (performance.now() - this._swiperWaitStart > maxWaitMs) {
        console.error('Swiper library not loaded');
        return;
      }
      this._swiperRaf = requestAnimationFrame(waitForSwiper);
    };

    this._swiperRaf = requestAnimationFrame(waitForSwiper);
  }

  init() {
    const swiperEl = this.querySelector('.lp-index-shop-by-category__swiper');
    if (!swiperEl || swiperEl.classList.contains('swiper-initialized')) return;

    if (!this.dotsWrap) {
      this.dotsWrap = this.section?.querySelector('[data-dots]');
    }

    const slides = swiperEl.querySelectorAll('.swiper-slide');
    if (slides.length < 2) {
      this.setControlsHidden(true);
      return;
    }

    const paginationConfig = this.dotsWrap
      ? {
        el: this.dotsWrap,
        clickable: true,
        bulletClass: 'lp-index-shop-by-category__dot',
        bulletActiveClass: 'lp-index-shop-by-category__dot--active',
        renderBullet: (index, className) =>
          `<button type="button" class="${className}" aria-label="Go to page ${index + 1}"></button>`,
      }
      : false;

    this.swiper = new Swiper(swiperEl, {
      // Fixed 240px cards on mobile for both layouts (CSS): 1.5 visible at 393.
      slidesPerView: 'auto',
      slidesPerGroup: 1,
      spaceBetween: 12,
      slidesOffsetAfter: this.getOffsetAfter(),
      watchOverflow: true,
      grabCursor: true,
      navigation: {
        prevEl: this.prevBtn,
        nextEl: this.nextBtn,
      },
      pagination: paginationConfig,
      breakpoints: {
        768: {
          slidesPerView: 2.15,
          slidesPerGroup: 2,
          spaceBetween: 16,
          slidesOffsetAfter: 40,
        },
        // Both layouts use fixed-width cards on desktop (CSS): CRO 313px,
        // Original 360 × 420 per the Figma card.
        1024: {
          slidesPerView: 'auto',
          slidesPerGroup: this.desktopGroup,
          spaceBetween: 16,
          slidesOffsetAfter: 0,
        },
      },
      on: {
        init: () => requestAnimationFrame(() => this.syncControls()),
        resize: () => this.syncControls(),
        breakpoint: () => this.syncControls(),
      },
    });
  }

  getActiveSlidesPerGroup() {
    const width = window.innerWidth;
    const bp = this.swiper.params.breakpoints || {};

    if (width >= 1024) return bp[1024]?.slidesPerGroup ?? this.desktopGroup ?? 3;
    if (width >= 768) return bp[768]?.slidesPerGroup ?? 2;
    return this.swiper.params.slidesPerGroup ?? 1;
  }

  // Width of the slide row that falls outside the visible track.
  getHiddenWidth() {
    const slides = [...this.swiper.slides];
    const gap = Number(this.swiper.params.spaceBetween) || 0;
    const rowWidth = slides.reduce((sum, slide) => sum + slide.offsetWidth, 0) + gap * (slides.length - 1);
    return rowWidth - this.swiper.el.clientWidth;
  }

  needsPagination() {
    const slideCount = this.swiper.slides.length;
    if (slideCount < 2) return false;

    // Fixed-width cards (both layouts on desktop), so "4 slides, 4 per group"
    // says nothing about whether they fit: on narrower desktops the last cards
    // are cut off. Turn the slider on once 10% or more of a card is hidden.
    if (this.swiper.params.slidesPerView === 'auto') {
      const hiddenWidth = this.getHiddenWidth();
      const cardWidth = this.swiper.slides[0].offsetWidth;
      return hiddenWidth >= cardWidth * HIDDEN_CARD_THRESHOLD;
    }

    return slideCount > this.getActiveSlidesPerGroup();
  }

  syncControls() {
    if (!this.swiper) return;

    const needsNav = this.needsPagination();
    const offset = needsNav ? this.getOffsetAfter() : 0;
    const width = window.innerWidth;

    this.swiper.params.slidesOffsetAfter = offset;
    if (width >= 1024 && this.swiper.params.breakpoints?.[1024]) {
      this.swiper.params.breakpoints[1024].slidesOffsetAfter = offset;
    } else if (width >= 768 && this.swiper.params.breakpoints?.[768]) {
      this.swiper.params.breakpoints[768].slidesOffsetAfter = needsNav ? 40 : 0;
    }

    this.swiper.update();

    if (this.swiper.pagination) {
      this.swiper.pagination.render();
      this.swiper.pagination.update();
    }

    this.setControlsHidden(!needsNav);
  }

  setControlsHidden(hidden) {
    const dotsInner = this.dotsWrap?.closest('.lp-index-shop-by-category__inner');

    if (this.dotsWrap) this.dotsWrap.hidden = hidden;
    if (dotsInner) dotsInner.hidden = hidden;
    if (this.prevBtn) this.prevBtn.hidden = hidden;
    if (this.nextBtn) this.nextBtn.hidden = hidden;
  }

  getOffsetAfter() {
    const width = window.innerWidth;

    // CRO shows four whole cards inside the padded track, so there is nothing
    // to peek past; the classic layout keeps its peeking card.
    if (width >= 1024 && !this.desktopPeek) {
      return 0;
    }

    if (width >= 768) {
      return 40;
    }

    return 16;
  }

  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }
}

if (!customElements.get('lp-category-carousel')) {
  customElements.define('lp-category-carousel', LpCategoryCarousel);
}
